import { Link } from "react-router-dom";
import Page from "@/components/layout/Page";
import anxietyWordcloud from "@/assets/mental-health/anxiety-wordcloud.png";
import nhsBadge from "@/assets/nhs-mental-health-badge.png";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function AnxietyStress() {
  return (
    <>
      <SEOHead
        title="Anxiety & Stress Relief | Tactile Sensory Tools | Samboards"
        description="Handmade tactile sensory tools for anxiety and stress management. Proven to reduce stress through touch, improve calm and focus. NHS-backed support. UK made."
        canonical="https://samboards.co.uk/mental-health/anxiety-stress"
        keywords="anxiety relief tools, stress management tools, tactile anxiety tools, sensory stress relief, ADHD anxiety, anxiety fidget tools, NHS mental health, UK handmade"
        ogImage={anxietyWordcloud}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Mental Health", url: "/mental-health/anxiety-stress" },
          { name: "Anxiety & Stress", url: "/mental-health/anxiety-stress" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <Page>
        <section className="w-full pb-16 sm:pb-20 md:pb-24">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
            <h1 className="text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.1] font-bold page-title-gradient flex-1">Anxiety & Stress</h1>
            <a 
              href="https://www.nhs.uk/mental-health/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 transition-all duration-200 hover:scale-105 hover:opacity-90 touch-manipulation"
              aria-label="NHS Mental Health Support"
            >
              <img 
                src={nhsBadge} 
                alt="NHS - Here to help with mental health" 
                className="h-12 sm:h-16 md:h-20 w-auto object-contain"
              />
            </a>
          </div>
          
          <div className="mental-health-hero-image">
            <img 
              src={anxietyWordcloud} 
              alt="Anxiety and stress related mental health conditions illustrated through word cloud"
            />
          </div>
          
          <div className="space-y-6 max-w-3xl">
            <p className="text-xl text-muted-foreground leading-relaxed">
              In today's fast-paced world, stress and anxiety have become increasingly common. These conditions can significantly impact our mental and physical well-being, leaving us feeling overwhelmed, exhausted, and isolated.
            </p>
            
            <p className="text-lg leading-relaxed">
              At Samboards, we understand the challenges individuals face when dealing with stress and anxiety. Our sensory products are designed to reduce anxiety, manage stress, and keep focus through tactile engagement and sensory stimulation.
            </p>
            
            <p className="text-lg leading-relaxed">
              The gentle, repetitive motions of touching and exploring different textures can help calm the nervous system, providing a natural way to self-regulate emotions and reduce feelings of overwhelm. Our products offer a quiet, non-disruptive way to manage stress in any environment.
            </p>
            
            <p className="text-lg leading-relaxed">
              Whether you're at work, at home, or on the go, Samboards provide a discreet tool for grounding yourself during moments of heightened anxiety or stress. The tactile feedback helps redirect anxious thoughts and provides a physical focal point for mindfulness.
            </p>
          </div>

          {/* Related Resources Section */}
          <div className="mt-16 pt-12 border-t border-slate-700/40">
            <h2 className="text-3xl font-semibold mb-3 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Recommended Products for Anxiety & Stress
            </h2>
            <p className="text-slate-300/80 mb-8 max-w-2xl">
              These sensory tools are particularly effective for managing anxiety and reducing stress through tactile engagement.
            </p>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/samboards"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Samboards</h3>
                <p className="text-sm text-slate-200/90 mb-4">Full-size sensory boards for everyday regulation, grounding and calm.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/sensory-pads"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Sensory Pads</h3>
                <p className="text-sm text-slate-200/90 mb-4">Soft tactile pads for gentle, calming touch and low-arousal stress management.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </Page>
    </div>
    </>
  );
}
